if (!customElements.get('pickup-availability')) {
  let nextId = 0;
  customElements.define('pickup-availability', class extends HTMLElement {
    connectedCallback() {
      this.errorHtml ||= this.querySelector('template')?.content.firstElementChild?.cloneNode(true);
      this.instanceId ||= 'pickup-' + (++nextId);
      this.onRefresh ||= () => this.fetchAvailability(this.dataset.variantId);
      this.fetchAvailability(this.dataset.variantId);
    }
    disconnectedCallback() {
      this.generation = (this.generation || 0) + 1;
      this.controller?.abort();
      clearTimeout(this.timeout);
      this.removeDrawer();
    }
    removeDrawer() {
      this.drawer?.hide();
      this.drawer?.modal?.remove();
      this.drawer?.remove();
      this.drawer = null;
    }
    update(variant) {
      this.dataset.variantId = variant?.id ? String(variant.id) : '';
      this.fetchAvailability(this.dataset.variantId);
    }
    async fetchAvailability(variantId) {
      const generation = this.generation = (this.generation || 0) + 1;
      this.controller?.abort();
      clearTimeout(this.timeout);
      this.removeDrawer();
      this.replaceChildren();
      this.removeAttribute('available');
      this.removeAttribute('aria-busy');
      if (!/^\d+$/.test(String(variantId || ''))) return;
      this.dataset.variantId = String(variantId);
      const controller = this.controller = new AbortController();
      this.setAttribute('aria-busy', 'true');
      const timeout = this.timeout = setTimeout(() => controller.abort(), 5000);
      try {
        const root = (this.dataset.rootUrl || '/').replace(/\/?$/, '/');
        const response = await fetch(root + 'variants/' + variantId + '/?section_id=pickup-availability', { signal: controller.signal });
        if (!response.ok) throw new Error('Unavailable');
        const document = new DOMParser().parseFromString(await response.text(), 'text/html');
        const section = document.querySelector('.shopify-section');
        if (!section) throw new Error('Missing section');
        if (generation !== this.generation || !this.isConnected) return;
        this.renderPreview(section);
      } catch (error) {
        if (generation === this.generation && this.isConnected) this.renderError();
      } finally {
        clearTimeout(timeout);
        if (generation === this.generation) this.removeAttribute('aria-busy');
      }
    }
    renderError() {
      if (!this.errorHtml) return;
      this.replaceChildren(this.errorHtml.cloneNode(true));
      this.setAttribute('available', '');
      this.querySelector('button')?.addEventListener('click', this.onRefresh);
    }
    renderPreview(section) {
      const preview = section.querySelector('pickup-availability-preview');
      const drawer = section.querySelector('pickup-availability-drawer');
      if (!preview) return;
      if (!drawer) throw new Error('Missing drawer');
      const heading = drawer.querySelector('[id="PickupAvailabilityHeading"]');
      if (heading) {
        heading.id = this.instanceId + '-heading';
        drawer.setAttribute('aria-labelledby', heading.id);
      }
      drawer.id = this.instanceId + '-drawer';
      drawer.setAttribute('inert', '');
      drawer.setAttribute('aria-hidden', 'true');
      const button = preview.querySelector('button');
      if (button) {
        button.id = this.instanceId + '-button';
        button.type = 'button';
        button.setAttribute('aria-controls', drawer.id);
        button.setAttribute('aria-expanded', 'false');
        button.addEventListener('click', () => this.drawer?.show(button));
      }
      for (const value of (this.dataset.productPageColorScheme || '').split(/\s+/).filter(Boolean)) drawer.classList.add(value);
      this.drawer = drawer;
      // A native modal keeps the drawer above sticky headers and app widgets.
      const modal = document.createElement('dialog');
      modal.className = 'pickup-availability-modal';
      modal.setAttribute('aria-labelledby', drawer.getAttribute('aria-labelledby'));
      drawer.removeAttribute('role');
      drawer.removeAttribute('aria-modal');
      drawer.modal = modal;
      modal.appendChild(drawer);
      document.body.appendChild(modal);
      this.replaceChildren(preview);
      this.setAttribute('available', '');
    }
  });
}
if (!customElements.get('pickup-availability-drawer')) {
  customElements.define('pickup-availability-drawer', class extends HTMLElement {
    connectedCallback() {
      this.onClose ||= () => this.hide();
      this.onKey ||= event => { if (event.key === 'Escape') this.hide(); };
      this.onBackdropClick ||= event => { if (event.target === this.modal) this.hide(); };
      this.onCancel ||= event => { event.preventDefault(); this.hide(); };
      this.modal?.addEventListener('click', this.onBackdropClick);
      this.modal?.addEventListener('cancel', this.onCancel);
      this.querySelector('.pickup-availability-drawer-button')?.addEventListener('click', this.onClose);
      this.addEventListener('keyup', this.onKey);
    }
    disconnectedCallback() {
      this.hide();
      this.querySelector('.pickup-availability-drawer-button')?.removeEventListener('click', this.onClose);
      this.removeEventListener('keyup', this.onKey);
      this.modal?.removeEventListener('click', this.onBackdropClick);
      this.modal?.removeEventListener('cancel', this.onCancel);
    }
    hide() {
      const wasOpen = this.hasAttribute('open');
      this.removeAttribute('open');
      this.setAttribute('inert', '');
      this.setAttribute('aria-hidden', 'true');
      if (this.modal?.open) this.modal.close();
      this.focusElement?.setAttribute('aria-expanded', 'false');
      if (wasOpen) {
        if (this.ownsScrollLock) document.body.classList.remove('overflow-hidden');
        removeTrapFocus(this.focusElement?.isConnected ? this.focusElement : undefined);
      }
    }
    show(element) {
      if (this.hasAttribute('open')) return;
      document.querySelectorAll('pickup-availability-drawer[open]').forEach(drawer => { if (drawer !== this) drawer.hide(); });
      this.focusElement = element;
      this.ownsScrollLock = !document.body.classList.contains('overflow-hidden');
      this.removeAttribute('inert');
      this.setAttribute('aria-hidden', 'false');
      this.setAttribute('open', '');
      element?.setAttribute('aria-expanded', 'true');
      document.body.classList.add('overflow-hidden');
      this.scrollTop = 0;
      this.modal.showModal();
      trapFocus(this, this.querySelector('.pickup-availability-drawer-button'));
      this.scrollTop = 0;
    }
  });
}
